import * as React from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";

// Import the original rich homepage
import Home from "@/pages/Home";

// Import from new app module
import { AppKeywordResearchPage, AppDashboardPage, AppWorkspacePage, WorkspaceDetailPage } from "@/modules/app";

// Import shared components
import NotFoundPage from "@/shared/pages/NotFoundPage";
import Layout from "@/components/global/Layout";

// Import auth pages
import { SignInPage, SignUpPage } from "@/pages/auth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * AppSectionLayout - A layout wrapper for the main application sections.
 * It uses the global Layout to provide consistent Navbar and Footer.
 * Passes isAppPage={true} to ensure app-specific styling (solid background, no particles).
 * Individual pages now handle their own authentication via AuthGuard.
 */
const AppSectionLayout: React.FC = () => {
  return (
    <Layout isAppPage={true} showParticles={false}> {/* Pass isAppPage and explicitly turn off particles */}
      <Outlet /> {/* Child routes (e.g., AppKeywordResearchPage) will render here */}
    </Layout>
  );
};

/**
 * App - Main application component
 */
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <Routes>
          {/* Use the original Home.tsx for the index route */}
          <Route index element={<Home />} /> 
          
          {/* Authentication Routes */}
          <Route path="/auth/sign-in" element={<SignInPage />} />
          <Route path="/auth/sign-up" element={<SignUpPage />} />
          
          {/* Redirect /app to /app/dashboard */}
          <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
          
          {/* Routes for the /app/* section, wrapped by AppSectionLayout (no more ProtectedRoute) */}
          <Route path="/app" element={<AppSectionLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="keyword-research" element={<AppKeywordResearchPage />} />
            <Route path="dashboard" element={<AppDashboardPage />} />
            <Route path="workspace" element={<AppWorkspacePage />} />
            <Route path="workspace/:workspaceId" element={<WorkspaceDetailPage />} />
            {/* Content writing routes removed */}
            {/* Handle any other /app/* routes */}
            <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
          </Route>
         
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;